(() => {
  const ENDPOINT = 'https://cdn.judge.me/reviews/reviews_for_widget';
  const PAGE_SIZE = 30;
  const CONCURRENT_REQUESTS = 3;
  const STEP = 6;
  const STEP_PHONE = 4;
  const MIN_TOPIC_COUNT = 3;
  const MAX_TOPICS = 8;
  const MAX_TAGS = 3;
  const MAX_PICTURES = 5;
  const STACK_SIZE = 4;
  const SWIPE_DISTANCE = 40;
  const EASING = 'cubic-bezier(0.23, 1, 0.32, 1)';
  const RATING_JUMP_SETTLE_MS = 2000;
  const EARLY_LOAD_DELAY_MS = 2000;
  const RATING_JUMP_CANCEL_EVENTS = ['wheel', 'touchstart', 'keydown'];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ProductReviews extends HTMLElement {
    connectedCallback() {
      this.locale = this.getAttribute('locale') || 'de';
      this.designMode = this.hasAttribute('design-mode');
      this.connectionId = (this.connectionId || 0) + 1;
      this.requestController = new AbortController();

      this.templates = {};
      this.querySelectorAll('template[data-reviews-template]').forEach((template) => {
        this.templates[template.dataset.reviewsTemplate] = template;
      });
      this.overview = this.querySelector('[data-reviews-overview]');
      this.averageText = this.querySelector('[data-reviews-average]');
      this.overviewStars = this.querySelector('[data-reviews-stars]');
      this.totalText = this.querySelector('[data-reviews-total]');
      this.shareText = this.querySelector('[data-reviews-share]');
      this.barRows = [...this.querySelectorAll('[data-star]')];
      this.topicsBox = this.querySelector('[data-reviews-topics]');
      this.topicChips = [...this.querySelectorAll('[data-topic]')];
      this.photoButton = this.querySelector('[data-photo-filter]');
      this.photoStack = this.querySelector('[data-reviews-photo-stack]');
      this.photoAny = this.querySelector('[data-reviews-photo-any]');
      this.photoCount = this.querySelector('[data-reviews-photo-count]');
      this.photoSort = this.querySelector('[data-reviews-photo-sort]');
      this.countText = this.querySelector('[data-reviews-count]');
      this.filterText = this.querySelector('[data-reviews-filters]');
      this.resetButton = this.querySelector('[data-reviews-reset]');
      this.errorText = this.querySelector('[data-reviews-error]');
      this.sortSelect = this.querySelector('[data-reviews-sort]');
      this.list = this.querySelector('[data-reviews-list]');
      this.emptyText = this.querySelector('[data-reviews-empty]');
      this.moreButton = this.querySelector('[data-reviews-more]');
      this.writeButton = this.querySelector('[data-reviews-write]');
      this.viewer = this.querySelector('[data-reviews-viewer]');
      this.viewerCard = this.querySelector('[data-viewer-card]');
      this.viewerScrim = this.querySelector('[data-viewer-scrim]');
      this.viewerStage = this.querySelector('[data-viewer-stage]');
      this.viewerImage = this.querySelector('[data-viewer-image]');
      this.viewerThumbs = this.querySelector('[data-viewer-thumbs]');
      this.viewerCounter = this.querySelector('[data-viewer-counter]');
      this.viewerName = this.querySelector('[data-viewer-name]');

      this.topicPatterns = this.topicChips
        .map((chip) => [chip.dataset.topic, this.keywordPattern(chip.dataset.keywords)])
        .filter(([, pattern]) => pattern);
      this.topicLabels = new Map(
        this.topicChips.map((chip) => [chip.dataset.topic, chip.querySelector('[data-topic-label]')?.textContent.trim() || ''])
      );
      this.phoneQuery = window.matchMedia('screen and (max-width: 999px)');
      this.pluralRules = this.localized((locale) => new Intl.PluralRules(locale));
      this.numberFormat = this.localized((locale) => new Intl.NumberFormat(locale));
      this.decimalFormat = this.localized(
        (locale) => new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      );
      this.dateFormat = this.localized(
        (locale) => new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
      );

      this.state = { star: 0, topic: '', photo: false, sort: 'new', shown: this.step() };
      this.reviews = [];
      this.visible = [];
      this.summary = null;
      this.allLoaded = false;
      this.allReviewsPromise = null;
      this.loading = false;
      this.viewerState = { photos: [], index: 0, from: null };
      this.viewerToken = 0;
      this.swipeStart = null;
      this.sortSelect.disabled = true;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerCancel.bind(this);
      this.onRatingLinkClick = this.onRatingLinkClick.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('pointerup', this.onPointerUp);
      this.addEventListener('pointercancel', this.onPointerCancel);
      document.addEventListener('keydown', this.onKeydown);
      this.connectWriteButton();
      this.connectRatingLinks();

      this.loadStarted = false;
      this.startLoad = this.startLoad.bind(this);
      this.scheduleEarlyLoad = this.scheduleEarlyLoad.bind(this);
      this.loadObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.startLoad();
        },
        { rootMargin: '800px 0px' }
      );
      this.loadObserver.observe(this);
      if (document.readyState === 'complete') this.scheduleEarlyLoad();
      else window.addEventListener('load', this.scheduleEarlyLoad, { once: true });
    }

    scheduleEarlyLoad() {
      clearTimeout(this.earlyLoadTimer);
      this.earlyLoadTimer = setTimeout(this.startLoad, EARLY_LOAD_DELAY_MS);
    }

    startLoad() {
      if (this.loadStarted || !this.isConnected) return;
      this.loadStarted = true;
      clearTimeout(this.earlyLoadTimer);
      this.loadObserver?.disconnect();
      this.load();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('pointerup', this.onPointerUp);
      this.removeEventListener('pointercancel', this.onPointerCancel);
      document.removeEventListener('keydown', this.onKeydown);
      this.ratingLinks?.forEach((link) => link.removeEventListener('click', this.onRatingLinkClick));
      this.cancelRatingJump?.();
      this.closeViewer(false);
      this.loadObserver?.disconnect();
      clearTimeout(this.earlyLoadTimer);
      window.removeEventListener('load', this.scheduleEarlyLoad);
      this.writeObserver?.disconnect();
      this.formObserver?.disconnect();
      this.requestController?.abort();
      this.connectionId += 1;
      this.allReviewsPromise = null;
    }

    localized(create) {
      try {
        return create(this.locale);
      } catch (error) {
        return create('en');
      }
    }

    keywordPattern(keywords) {
      if (!keywords) return null;
      try {
        return new RegExp(keywords, 'i');
      } catch (error) {
        return null;
      }
    }

    step() {
      return this.phoneQuery.matches ? STEP_PHONE : STEP;
    }

    fill(text, values) {
      return Object.entries(values).reduce((result, [key, value]) => result.split(`[[${key}]]`).join(value), text || '');
    }

    plural(element, count) {
      const category = this.pluralRules.select(count);
      const key = `phrase${category.charAt(0).toUpperCase()}${category.slice(1)}`;
      return element.dataset[key] || element.dataset.phraseOther || '';
    }

    number(value) {
      return this.numberFormat.format(value);
    }

    rating(value) {
      return Number.isInteger(value) ? this.number(value) : this.decimalFormat.format(value);
    }

    date(value) {
      return value ? this.dateFormat.format(new Date(`${value}T12:00:00`)) : '';
    }

    cloneTemplate(name) {
      return this.templates[name].content.firstElementChild.cloneNode(true);
    }

    fillTemplate(name, values) {
      const fragment = this.templates[name].content.cloneNode(true);
      const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) walker.currentNode.textContent = this.fill(walker.currentNode.textContent, values);
      return fragment;
    }

    setStars(element, rating) {
      element.style.setProperty('--product-reviews-fill', `${Math.round((rating / 5) * 1000) / 10}%`);
      element.setAttribute('role', 'img');
      element.setAttribute('aria-label', this.fill(element.dataset.phrase, { rating: this.rating(rating) }));
      element.removeAttribute('aria-hidden');
    }

    setCount(element, count) {
      const phrase = this.plural(element, count);
      const parts = phrase.split('[[count]]');
      if (parts.length < 2) {
        element.textContent = phrase;
        return;
      }
      const number = document.createElement('b');
      number.textContent = this.number(count);
      element.replaceChildren(parts[0], number, parts.slice(1).join(this.number(count)));
    }

    animate(element, keyframes, options) {
      if (reducedMotion.matches || !element?.animate) return;
      element.animate(keyframes, options);
    }

    pageUrl(page, stars = 0) {
      const params = new URLSearchParams({
        product_id: this.getAttribute('product-id'),
        page: String(page),
        other_language_page: String(page),
        per_page: String(PAGE_SIZE),
        sort_by: 'created_at',
        sort_dir: 'desc',
        translation_locale: this.locale,
        skip_other_languages: 'false',
        widget_theme: 'standard',
        shop_domain: this.getAttribute('shop-domain'),
        platform: 'shopify',
      });
      if (stars) params.set('filter_rating', String(stars));
      return `${ENDPOINT}?${params}`;
    }

    async fetchPage(page, stars = 0) {
      const response = await fetch(this.pageUrl(page, stars), { signal: this.requestController?.signal });
      if (!response.ok) throw new Error(`Reviews request failed with ${response.status}`);
      return response.json();
    }

    async load() {
      const connectionId = this.connectionId;
      try {
        const first = await this.fetchPage(1);
        if (connectionId !== this.connectionId || !this.isConnected) return;
        this.reviews = this.normalizePages([first]);
        this.summary = {
          count: Number(first.number_of_reviews) || this.reviews.length,
          average: Number(first.average_rating) || 0,
          histogram: [5, 4, 3, 2, 1].map(
            (stars) => Number((first.histogram || []).find((row) => Number(row.rating) === stars)?.frequency) || 0
          ),
        };
        this.allLoaded = this.pageCount(first) <= 1 && this.reviews.length >= this.summary.count;
        this.safeShown = this.allLoaded ? this.reviews.length : Math.min(PAGE_SIZE, this.reviews.length);
        if (this.pageCount(first) <= 1 && !this.allLoaded && this.showNativeReviews(false)) return;
        this.topics = this.allLoaded ? this.countTopics() : [];
        this.renderOverview();
        this.dataset.state = 'ready';
        this.render();
        this.setLoading(false);
      } catch (error) {
        if (connectionId !== this.connectionId || !this.isConnected) return;
        if (this.showNativeReviews(false)) return;
        this.dataset.state = 'error';
        this.list.replaceChildren();
        this.list.setAttribute('aria-busy', 'false');
        this.showLoadError();
      }
    }

    pageCount(data) {
      return Math.max(data.primary_language_pagination?.total_pages || 1, data.other_language_pagination?.total_pages || 1);
    }

    normalizePages(pages) {
      const reviews = new Map();
      pages.forEach((data) => {
        const raw = Array.isArray(data.primary_language_reviews)
          ? [...data.primary_language_reviews, ...(data.other_language_reviews || [])]
          : data.reviews || [];
        raw.forEach((review) => {
          const key =
            review.uuid ||
            JSON.stringify([review.reviewer_name, review.created_at, review.rating, review.title, review.body_html || review.body]);
          if (!reviews.has(key)) reviews.set(key, this.normalize(review, data.review_translations?.[review.uuid]));
        });
      });
      return [...reviews.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }

    async ensureAllReviews() {
      if (this.allLoaded) return true;
      if (this.allReviewsPromise) return this.allReviewsPromise;
      const promise = this.collectReviews();
      this.allReviewsPromise = promise;
      try {
        return await promise;
      } finally {
        if (this.allReviewsPromise === promise) this.allReviewsPromise = null;
      }
    }

    async collectReviews() {
      const connectionId = this.connectionId;
      this.setLoading(true);
      try {
        const pages = [];
        const queue = [5, 4, 3, 2, 1]
          .filter((stars) => this.summary.histogram[5 - stars] > 0)
          .map((stars) => ({ page: 1, stars }));
        if (!queue.length) queue.push({ page: 1, stars: 0 });
        while (queue.length) {
          const batch = queue.splice(0, CONCURRENT_REQUESTS);
          const responses = await Promise.all(batch.map(({ page, stars }) => this.fetchPage(page, stars)));
          if (connectionId !== this.connectionId || !this.isConnected) return false;
          responses.forEach((data, index) => {
            pages.push(data);
            if (batch[index].page !== 1) return;
            for (let page = 2; page <= this.pageCount(data); page += 1) queue.push({ page, stars: batch[index].stars });
          });
        }
        const reviews = this.normalizePages(pages);
        if (reviews.length < this.summary.count) {
          if (this.showNativeReviews(true)) return false;
          throw new Error('Review pagination did not expose the complete review set');
        }
        this.reviews = reviews;
        this.allLoaded = true;
        this.topics = this.countTopics();
        this.renderOverview();
        return true;
      } catch (error) {
        if (connectionId !== this.connectionId || !this.isConnected) return false;
        this.showLoadError();
        return false;
      } finally {
        if (connectionId === this.connectionId && this.isConnected) this.setLoading(false);
      }
    }

    setLoading(loading) {
      this.loading = loading;
      this.list.setAttribute('aria-busy', String(loading));
      this.moreButton.disabled = loading;
      this.sortSelect.disabled = loading;
      this.overview.querySelectorAll('button').forEach((button) => {
        button.disabled = loading || Boolean(button.dataset.star && !this.summary.histogram[5 - Number(button.dataset.star)]);
      });
    }

    showLoadError() {
      this.countText.hidden = true;
      this.filterText.hidden = true;
      this.resetButton.hidden = true;
      this.errorText.hidden = false;
    }

    showNativeReviews(scroll) {
      if (!this.judgeMeSection || this.designMode) return false;
      this.judgeMeSection.hidden = false;
      const section = this.closest('.shopify-section');
      if (section) {
        section.hidden = true;
        document.querySelectorAll('a[href]').forEach((link) => {
          if (link.getAttribute('href') === `#${section.id}`) link.setAttribute('href', '#judgeme_product_reviews');
        });
      } else {
        this.hidden = true;
      }
      if (scroll) this.judgeMeSection.scrollIntoView({ block: 'start', behavior: 'auto' });
      return true;
    }

    normalize(review, translations) {
      const translation = translations?.[this.locale] || translations?.[this.locale.split('-')[0]];
      const translatedBody = typeof translation?.body === 'string' && translation.body.trim() ? translation.body : null;
      const translatedTitle = typeof translation?.title === 'string' && translation.title.trim() ? translation.title : null;
      const body = new DOMParser()
        .parseFromString(translatedBody || review.body_html || review.body || '', 'text/html')
        .body.textContent.replace(/\s+/g, ' ')
        .trim();
      const title = (translatedTitle || review.title || '').trim();
      const photos = (review.pictures_urls || [])
        .map((picture) => (typeof picture === 'string' ? picture : picture.compact || picture.original || picture.huge || ''))
        .filter(Boolean);
      const text = `${title} ${body}`;
      return {
        rating: Number(review.rating) || 0,
        title,
        body,
        name: (review.reviewer_name || '').trim(),
        date: (review.created_at || '').slice(0, 10),
        createdAt: review.created_at || '',
        verified: review.verified_buyer === true || review.verified_buyer === 'true',
        photos,
        topics: this.topicPatterns.filter(([, pattern]) => pattern.test(text)).map(([id]) => id),
      };
    }

    countTopics() {
      return this.topicPatterns
        .map(([id]) => ({ id, count: this.reviews.filter((review) => review.topics.includes(id)).length }))
        .filter((topic) => topic.count >= MIN_TOPIC_COUNT)
        .sort((a, b) => b.count - a.count)
        .slice(0, MAX_TOPICS);
    }

    photoUrl(url, width) {
      return /width=\d+/.test(url) ? url.replace(/width=\d+/, `width=${width}`) : url;
    }

    renderOverview() {
      const { count, average, histogram } = this.summary;
      const highest = Math.max(...histogram, 1);
      const goodShare = count ? Math.floor(((histogram[0] + histogram[1]) / count) * 100) : 0;

      this.averageText.textContent = this.decimalFormat.format(average);
      this.setStars(this.overviewStars, average);
      this.totalText.textContent = this.fill(this.plural(this.totalText, count), { count: this.number(count) });
      this.shareText.replaceChildren(this.fillTemplate('share', { percent: this.number(goodShare) }));
      this.barRows.forEach((row) => {
        const amount = histogram[5 - Number(row.dataset.star)];
        const width = amount ? Math.max(2, Math.round((amount / highest) * 100)) : 0;
        row.style.setProperty('--product-reviews-bar', `${width}%`);
        row.querySelector('[data-reviews-bar-count]').textContent = this.number(amount);
      });
      this.renderTopics();
      this.renderPhotoFilter();
    }

    renderTopics() {
      if (!this.topicsBox) return;
      const topics = this.topics || [];
      const counts = new Map(topics.map((topic) => [topic.id, topic.count]));
      this.topicsBox.hidden = !topics.length;
      this.topicChips.forEach((chip) => {
        const count = counts.get(chip.dataset.topic);
        chip.hidden = count === undefined;
        if (count !== undefined) chip.querySelector('[data-topic-count]').textContent = this.number(count);
      });
      const group = this.topicChips[0]?.parentElement;
      topics.forEach((topic) => {
        const chip = this.topicChips.find((element) => element.dataset.topic === topic.id);
        if (group && chip) group.append(chip);
      });
    }

    renderPhotoFilter() {
      const withPhotos = this.reviews.filter((review) => review.photos.length);
      const thumbs = withPhotos.flatMap((review) => review.photos).slice(0, STACK_SIZE);
      this.photoButton.hidden = !thumbs.length;
      this.photoSort.hidden = !thumbs.length;
      this.photoStack.replaceChildren(
        ...thumbs.map((url) => {
          const image = this.cloneTemplate('stack-image');
          image.src = this.photoUrl(url, 120);
          return image;
        })
      );
      this.photoAny.hidden = this.allLoaded;
      this.photoCount.hidden = !this.allLoaded;
      if (this.allLoaded) {
        this.photoCount.textContent = this.fill(this.plural(this.photoCount, withPhotos.length), {
          count: this.number(withPhotos.length),
        });
      }
    }

    filtered() {
      const { star, topic, photo, sort } = this.state;
      let reviews = this.reviews.filter(
        (review) => (!star || review.rating === star) && (!topic || review.topics.includes(topic)) && (!photo || review.photos.length)
      );
      if (sort === 'best') reviews = [...reviews].sort((a, b) => b.rating - a.rating || b.body.length - a.body.length);
      else if (sort === 'critical') reviews = [...reviews].sort((a, b) => a.rating - b.rating || b.body.length - a.body.length);
      else if (sort === 'photo') reviews = [...reviews].sort((a, b) => (b.photos.length ? 1 : 0) - (a.photos.length ? 1 : 0));
      return reviews;
    }

    card(review, index) {
      const card = this.cloneTemplate('card');
      const nameElement = card.querySelector('[data-review-name]');
      const name = review.name || nameElement.dataset.anonymous || '';
      const titleRepeatsBody =
        !review.title || review.body.toLowerCase().startsWith(review.title.toLowerCase().replace(/[.\u2026\s]+$/, ''));
      const date = card.querySelector('[data-review-date]');
      const pictures = review.photos.slice(0, MAX_PICTURES);
      const picturesBox = card.querySelector('[data-review-pictures]');
      const tagsBox = card.querySelector('[data-review-tags]');

      card.querySelector('[data-review-initial]').textContent = name.charAt(0).toUpperCase();
      nameElement.textContent = name;
      if (!review.verified) card.querySelector('[data-review-verified]').remove();
      this.setStars(card.querySelector('[data-review-stars]'), review.rating);
      date.dateTime = review.date;
      date.textContent = this.date(review.date);
      if (titleRepeatsBody) card.querySelector('[data-review-title]').remove();
      else card.querySelector('[data-review-title]').textContent = review.title;
      card.querySelector('[data-review-text]').textContent = review.body;

      if (pictures.length) {
        const phrase = this.templates.picture.content.firstElementChild.dataset.phrase;
        picturesBox.append(
          ...pictures.map((url, photoIndex) => {
            const button = this.cloneTemplate('picture');
            button.removeAttribute('data-phrase');
            button.dataset.review = String(index);
            button.dataset.photo = String(photoIndex);
            button.setAttribute(
              'aria-label',
              this.fill(phrase, { index: this.number(photoIndex + 1), total: this.number(pictures.length) })
            );
            button.querySelector('img').src = this.photoUrl(url, 320);
            return button;
          })
        );
      } else {
        picturesBox.remove();
      }

      const topics = review.topics.slice(0, MAX_TAGS);
      if (topics.length) {
        tagsBox.append(
          ...topics.map((topic) => {
            const tag = this.cloneTemplate('tag');
            tag.textContent = this.topicLabels.get(topic) || '';
            tag.classList.toggle('is-active', topic === this.state.topic);
            return tag;
          })
        );
      } else {
        tagsBox.remove();
      }
      return card;
    }

    render(scrollToList) {
      const reviews = this.filtered();
      this.visible = reviews;
      const { star, topic, photo, shown } = this.state;
      const parts = [];
      if (star) parts.push(this.barRows.find((row) => Number(row.dataset.star) === star)?.dataset.filterLabel || '');
      if (topic) parts.push(this.topicChips.find((chip) => chip.dataset.topic === topic)?.dataset.filterLabel || '');
      if (photo) parts.push(this.photoButton.dataset.filterLabel || '');

      const count = this.allLoaded ? reviews.length : this.summary.count;
      this.errorText.hidden = true;
      this.countText.hidden = false;
      this.filterText.hidden = false;
      this.setCount(this.countText, count);
      this.filterText.textContent = parts.filter(Boolean).join(' ');
      this.resetButton.hidden = !parts.length;

      this.list.replaceChildren(...reviews.slice(0, shown).map((review, index) => this.card(review, index)));
      this.emptyText.hidden = reviews.length > 0;
      const clamped = [...this.list.querySelectorAll('[data-review-text]')].filter((text) => text.scrollHeight > text.clientHeight + 2);
      clamped.forEach((text) => {
        text.nextElementSibling.hidden = false;
      });

      const remaining = count - Math.min(shown, reviews.length);
      this.moreButton.hidden = remaining <= 0;
      this.moreButton.textContent = this.fill(this.moreButton.dataset.phrase, { remaining: this.number(remaining) });

      this.barRows.forEach((row) => row.setAttribute('aria-pressed', String(Number(row.dataset.star) === star)));
      this.topicChips.forEach((chip) => chip.setAttribute('aria-pressed', String(chip.dataset.topic === topic)));
      this.photoButton.setAttribute('aria-pressed', String(photo));
      this.animate(this.list, [{ opacity: 0.35 }, { opacity: 1 }], { duration: 180, easing: EASING });

      const bar = this.querySelector('[data-reviews-bar]');
      if (scrollToList && bar && this.phoneQuery.matches) {
        const top = bar.getBoundingClientRect().top;
        if (top > window.innerHeight * 0.72 || top < 0) {
          bar.scrollIntoView({ block: 'center', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        }
      }
    }

    async onClick(event) {
      if (event.target.closest('[data-viewer-scrim]')) {
        this.closeViewer();
        return;
      }
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button === this.writeButton) {
        this.openReviewForm();
        return;
      }
      if (this.loading || !this.summary) return;
      const filters = button.dataset.star || button.dataset.topic || 'photoFilter' in button.dataset;
      const needsMore = button === this.moreButton && this.state.shown + this.step() > this.safeShown;
      if ((filters || needsMore) && !this.allLoaded) {
        const restoreFocus = document.activeElement === button;
        if (!(await this.ensureAllReviews()) || !this.isConnected) return;
        if (restoreFocus) {
          const selector = button.dataset.star
            ? `[data-star="${button.dataset.star}"]`
            : button.dataset.topic
              ? `[data-topic="${button.dataset.topic}"]`
              : '[data-photo-filter]';
          (button === this.moreButton ? button : this.overview.querySelector(selector))?.focus({ preventScroll: true });
        }
      }

      if (button.dataset.star) {
        this.state.star = this.state.star === Number(button.dataset.star) ? 0 : Number(button.dataset.star);
        this.resetPaging(true);
      } else if (button.dataset.topic) {
        this.state.topic = this.state.topic === button.dataset.topic ? '' : button.dataset.topic;
        this.resetPaging(true);
      } else if ('photoFilter' in button.dataset) {
        this.state.photo = !this.state.photo;
        this.resetPaging(true);
      } else if ('reviewsReset' in button.dataset) {
        Object.assign(this.state, { star: 0, topic: '', photo: false });
        this.resetPaging(false);
      } else if (button === this.moreButton) {
        this.state.shown += this.step();
        this.render();
      } else if ('reviewReadMore' in button.dataset) {
        this.toggleText(button);
      } else if (button.dataset.review !== undefined) {
        const review = this.visible[Number(button.dataset.review)];
        if (review) this.openViewer(review, Number(button.dataset.photo), button);
      } else if ('viewerClose' in button.dataset) {
        this.closeViewer();
      } else if (button.dataset.viewerStep) {
        this.showPhoto(this.viewerState.index + Number(button.dataset.viewerStep), true);
      } else if (button.dataset.viewerThumb) {
        this.showPhoto(Number(button.dataset.viewerThumb), true);
      }
    }

    toggleText(button) {
      const text = button.previousElementSibling;
      const expanded = !text.hasAttribute('data-expanded');
      text.toggleAttribute('data-expanded', expanded);
      button.setAttribute('aria-expanded', String(expanded));
      button.querySelector('[data-read-more]').hidden = expanded;
      button.querySelector('[data-read-less]').hidden = !expanded;
    }

    async onChange(event) {
      if (event.target !== this.sortSelect || this.loading || !this.summary) return;
      const sort = this.sortSelect.value;
      if (!this.allLoaded && !(await this.ensureAllReviews())) {
        this.sortSelect.value = this.state.sort;
        return;
      }
      if (!this.isConnected) return;
      this.state.sort = sort;
      this.resetPaging(false);
    }

    resetPaging(scrollToList) {
      this.state.shown = this.step();
      this.render(scrollToList);
    }

    openViewer(review, index, from) {
      const photos = review.photos.slice(0, MAX_PICTURES);
      const name = review.name || this.viewerName.dataset.anonymous || '';
      const date = this.viewer.querySelector('[data-viewer-date]');
      this.viewerState = { photos, index: 0, from };

      this.viewer.querySelector('[data-viewer-initial]').textContent = name.charAt(0).toUpperCase();
      this.viewerName.textContent = name;
      this.setStars(this.viewer.querySelector('[data-viewer-stars]'), review.rating);
      date.dateTime = review.date;
      date.textContent = this.date(review.date);
      this.viewer.classList.toggle('product-reviews__viewer--single', photos.length < 2);
      const phrase = this.templates.thumb.content.firstElementChild.dataset.phrase;
      this.viewerThumbs.replaceChildren(
        ...(photos.length > 1
          ? photos.map((url, photoIndex) => {
              const button = this.cloneTemplate('thumb');
              button.removeAttribute('data-phrase');
              button.dataset.viewerThumb = String(photoIndex);
              button.setAttribute('aria-label', this.fill(phrase, { index: this.number(photoIndex + 1) }));
              button.querySelector('img').src = this.photoUrl(url, 160);
              return button;
            })
          : [])
      );

      this.viewer.hidden = false;
      this.lockScroll();
      this.showPhoto(index, false);
      this.animate(this.viewerCard, [{ opacity: 0, transform: 'scale(0.96)' }, { opacity: 1, transform: 'none' }], {
        duration: 220,
        easing: EASING,
      });
      this.animate(this.viewerScrim, [{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease' });
      this.viewer.querySelector('[data-viewer-close]').focus({ preventScroll: true });
    }

    loadedImage(image) {
      return image?.complete && image.naturalWidth ? image : null;
    }

    stageRatio(image) {
      return image?.naturalWidth ? Math.min(1.5, Math.max(0.75, image.naturalWidth / image.naturalHeight)) : 0;
    }

    showPhoto(index, animate) {
      const { photos, from } = this.viewerState;
      if (!photos.length) return;
      this.viewerState.index = (index + photos.length) % photos.length;
      const position = this.viewerState.index;
      const url = photos[position];
      const token = ++this.viewerToken;
      const cardImages = from?.parentElement ? from.parentElement.querySelectorAll('img') : [];
      const preview = this.loadedImage(cardImages[position]) || this.loadedImage(this.viewerThumbs.querySelectorAll('img')[position]);
      const previewRatio = this.stageRatio(preview);
      if (previewRatio) this.viewerStage.style.setProperty('--product-reviews-ratio', String(previewRatio));

      this.viewerImage.src = this.photoUrl(url, 320);
      this.viewerImage.alt = this.fill(this.viewerImage.dataset.phrase, {
        index: this.number(position + 1),
        total: this.number(photos.length),
      });
      if (animate) this.animate(this.viewerImage, [{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: EASING });

      const large = new Image();
      large.onload = () => {
        if (token !== this.viewerToken || this.viewer.hidden) return;
        const ratio = this.stageRatio(large);
        if (ratio) this.viewerStage.style.setProperty('--product-reviews-ratio', String(ratio));
        this.viewerImage.src = large.src;
      };
      large.src = this.photoUrl(url, 1200);
      if (photos.length > 1) {
        const next = new Image();
        next.src = this.photoUrl(photos[(position + 1) % photos.length], 1200);
      }

      this.viewerCounter.textContent = photos.length > 1 ? `${this.number(position + 1)} / ${this.number(photos.length)}` : '';
      this.viewerThumbs.querySelectorAll('[data-viewer-thumb]').forEach((button, thumbIndex) => {
        button.setAttribute('aria-pressed', String(thumbIndex === position));
      });
    }

    lockScroll() {
      this.addedScrollLock = !document.documentElement.classList.contains('lock-all');
      if (this.addedScrollLock) document.documentElement.classList.add('lock-all');
    }

    unlockScroll() {
      if (this.addedScrollLock) document.documentElement.classList.remove('lock-all');
      this.addedScrollLock = false;
    }

    closeViewer(restoreFocus = true) {
      if (!this.viewer || this.viewer.hidden) return;
      this.viewer.hidden = true;
      this.viewerToken += 1;
      this.unlockScroll();
      this.viewerImage.removeAttribute('src');
      if (restoreFocus && this.viewerState.from?.isConnected) this.viewerState.from.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (this.viewer.hidden) return;
      if (event.key === 'Escape') {
        this.closeViewer();
      } else if (this.viewerState.photos.length > 1 && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
        this.showPhoto(this.viewerState.index + (event.key === 'ArrowRight' ? 1 : -1), false);
      } else if (event.key === 'Tab') {
        const focusable = [...this.viewer.querySelectorAll('button')].filter((button) => button.offsetParent);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    onPointerDown(event) {
      const onStage = event.target.closest('[data-viewer-stage]') && !event.target.closest('button');
      this.swipeStart = onStage ? event.clientX : null;
    }

    onPointerUp(event) {
      const distance = this.swipeStart === null ? 0 : event.clientX - this.swipeStart;
      this.swipeStart = null;
      if (this.viewerState.photos.length < 2 || Math.abs(distance) <= SWIPE_DISTANCE) return;
      this.showPhoto(this.viewerState.index + (distance < 0 ? 1 : -1), true);
    }

    onPointerCancel() {
      this.swipeStart = null;
    }

    connectWriteButton() {
      const widget = document.querySelector('.jdgm-review-widget, .jdgm-widget.jdgm-review-widget');
      this.judgeMeSection = widget ? widget.closest('.shopify-section') : null;
      if (this.judgeMeSection && !this.designMode) this.judgeMeSection.hidden = true;
      if (!widget || !this.judgeMeSection) {
        this.writeButton.hidden = true;
        return;
      }
      if (this.judgeMeWriteButton()) {
        this.showWriteButton();
        return;
      }
      this.writeObserver = new MutationObserver(() => {
        if (!this.judgeMeWriteButton()) return;
        this.showWriteButton();
        this.writeObserver.disconnect();
      });
      this.writeObserver.observe(widget, { childList: true, subtree: true });
    }

    showWriteButton() {
      this.writeButton.hidden = false;
      this.writeButton.removeAttribute('data-waiting');
    }

    judgeMeWriteButton() {
      return this.judgeMeSection?.querySelector('[data-testid="write-review-button"], .jm-action-buttons__button') || null;
    }

    connectRatingLinks() {
      const section = this.closest('.shopify-section');
      if (!section?.id) return;
      this.ratingLinks = [...document.querySelectorAll(`a[href="#judgeme_product_reviews"], a[href="#${section.id}"]`)];
      this.ratingLinks.forEach((link) => {
        link.setAttribute('href', `#${section.id}`);
        link.addEventListener('click', this.onRatingLinkClick);
      });
    }

    onRatingLinkClick() {
      const section = this.closest('.shopify-section');
      const heading = section?.querySelector('[data-reviews-heading]');
      if (!heading || section.hidden) return;
      heading.focus({ preventScroll: true });
      this.settleRatingJump(section);
    }

    settleRatingJump(section) {
      this.cancelRatingJump?.();
      let timer = 0;
      const cancel = () => {
        clearTimeout(timer);
        window.removeEventListener('scrollend', settle);
        RATING_JUMP_CANCEL_EVENTS.forEach((type) => window.removeEventListener(type, cancel));
        this.cancelRatingJump = null;
      };
      const settle = () => {
        cancel();
        if (section.isConnected && !section.hidden) section.scrollIntoView({ block: 'start', behavior: 'instant' });
      };
      timer = setTimeout(settle, RATING_JUMP_SETTLE_MS);
      window.addEventListener('scrollend', settle);
      RATING_JUMP_CANCEL_EVENTS.forEach((type) => window.addEventListener(type, cancel, { passive: true }));
      this.cancelRatingJump = cancel;
    }

    openReviewForm() {
      const judgeMeButton = this.judgeMeWriteButton();
      if (!judgeMeButton) return;
      this.formObserver?.disconnect();
      this.reviewModal = null;
      this.formObserver = new MutationObserver(() => this.syncReviewForm());
      this.formObserver.observe(document.body, { childList: true });
      document.querySelectorAll('.jdgm-write-review-modal').forEach((modal) => this.watchReviewModal(modal));
      judgeMeButton.click();
      this.syncReviewForm();
    }

    watchReviewModal(modal) {
      this.formObserver.observe(modal, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    }

    syncReviewForm() {
      const modal = [...document.querySelectorAll('.jdgm-write-review-modal')].find(
        (element) => getComputedStyle(element).display !== 'none'
      );
      if (modal && modal !== this.reviewModal) {
        if (!this.reviewModal) modal.querySelector('.jdgm-write-review-modal__close-btn')?.focus({ preventScroll: true });
        this.reviewModal = modal;
        this.watchReviewModal(modal);
      } else if (!modal && this.reviewModal) {
        this.reviewModal = null;
        this.formObserver.disconnect();
        this.writeButton.focus({ preventScroll: true });
      }
    }
  }

  if (!customElements.get('product-reviews')) customElements.define('product-reviews', ProductReviews);
})();
